import type { InventoryItem, PurchaseOrder, PurchaseOrderItem } from '../types';
import { inventoryService } from './database';

export interface ReceiveLinePreview {
  lineId: string;
  productName: string;
  quantity: number;
  action: 'add_stock' | 'create_new' | 'match_existing';
  inventoryItemId?: number;
  matchedName?: string;
}

export interface PurchaseOrderReceiveResult {
  stockUpdated: number;
  created: number;
  ambiguous: PurchaseOrderItem[];
  previews: ReceiveLinePreview[];
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function previewPurchaseOrderReceive(
  po: PurchaseOrder,
  inventory: InventoryItem[],
): ReceiveLinePreview[] {
  return po.items.map((line) => {
    if (line.inventoryItemId != null) {
      const inv = inventory.find((i) => i.id === line.inventoryItemId);
      return {
        lineId: line.lineId,
        productName: line.productName,
        quantity: line.quantity,
        action: 'add_stock',
        inventoryItemId: line.inventoryItemId,
        matchedName: inv?.name,
      };
    }
    const key = normalizeName(line.productName);
    const matches = inventory.filter((i) => normalizeName(i.name) === key);
    if (matches.length === 1) {
      return {
        lineId: line.lineId,
        productName: line.productName,
        quantity: line.quantity,
        action: 'match_existing',
        inventoryItemId: matches[0].id,
        matchedName: matches[0].name,
      };
    }
    if (matches.length > 1) {
      return {
        lineId: line.lineId,
        productName: line.productName,
        quantity: line.quantity,
        action: 'create_new',
        matchedName: `${matches.length} similar names — will create or pick on receive`,
      };
    }
    return {
      lineId: line.lineId,
      productName: line.productName,
      quantity: line.quantity,
      action: 'create_new',
    };
  });
}

export type ResolveAmbiguousLine = (
  line: PurchaseOrderItem,
) => Promise<{ inventoryItemId: number } | { create: Omit<InventoryItem, 'id' | 'lastUpdated'> }>;

export async function applyPurchaseOrderReceive(
  po: PurchaseOrder,
  inventory: InventoryItem[],
  supplierName: string,
  resolveAmbiguous?: ResolveAmbiguousLine,
): Promise<PurchaseOrderReceiveResult> {
  const previews = previewPurchaseOrderReceive(po, inventory);
  let stockUpdated = 0;
  let created = 0;
  const ambiguous: PurchaseOrderItem[] = [];

  for (const line of po.items) {
    const preview = previews.find((p) => p.lineId === line.lineId);
    let targetId = line.inventoryItemId ?? preview?.inventoryItemId;

    if (targetId == null && preview?.action === 'match_existing' && preview.matchedName?.includes('possible')) {
      ambiguous.push(line);
      if (resolveAmbiguous) {
        const resolved = await resolveAmbiguous(line);
        if ('inventoryItemId' in resolved) {
          targetId = resolved.inventoryItemId;
        } else {
          const newItem = await inventoryService.create({
            ...resolved.create,
            lastUpdated: Date.now(),
          } as Omit<InventoryItem, 'id'>);
          const id = typeof newItem.id === 'number' ? newItem.id : Number(newItem.id);
          targetId = id;
          created += 1;
        }
      } else {
        continue;
      }
    }

    if (targetId == null && line.inventoryItemId == null) {
      const key = normalizeName(line.productName);
      const matches = inventory.filter((i) => normalizeName(i.name) === key);
      if (matches.length === 1) {
        targetId = matches[0].id;
      }
    }

    if (targetId != null) {
      const inv = inventory.find((i) => i.id === targetId);
      const currentStock = inv?.stock ?? 0;
      await inventoryService.update(String(targetId), {
        stock: currentStock + line.quantity,
        costPerUnit: line.costPerUnit,
        supplier: supplierName || inv?.supplier,
        lastUpdated: Date.now(),
      });
      stockUpdated += 1;
      continue;
    }

    if (preview?.action === 'create_new' || targetId == null) {
      await inventoryService.create({
        name: line.productName,
        stock: line.quantity,
        unit: line.unit ?? 'units',
        lowStockThreshold: 10,
        costPerUnit: line.costPerUnit,
        supplier: supplierName,
        category: line.category,
        lastUpdated: Date.now(),
      } as Omit<InventoryItem, 'id'>);
      created += 1;
    }
  }

  return { stockUpdated, created, ambiguous, previews };
}

export function formatReceiveSummary(result: PurchaseOrderReceiveResult): string {
  const parts: string[] = [];
  if (result.stockUpdated > 0) {
    parts.push(`${result.stockUpdated} line${result.stockUpdated === 1 ? '' : 's'} added to stock`);
  }
  if (result.created > 0) {
    parts.push(`${result.created} new product${result.created === 1 ? '' : 's'} created in inventory`);
  }
  if (result.ambiguous.length > 0 && result.stockUpdated === 0 && result.created === 0) {
    parts.push(`${result.ambiguous.length} line(s) need manual resolution`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'No inventory changes applied';
}
